<template>
  <div
    class="app-form-card app-form-card--documents"
    :class="cardClass"
  >
    <div v-if="showHeader" class="app-form-card__header">
      <div class="app-form-card__title">{{ headerTitle }}</div>
    </div>
    <div class="app-form-card__body app-form-card__body--documents">
      <div class="attachment-wrap">
        <div class="attachment-layout">
          <div class="attachment-top">
            <div
              class="attachment-dropzone mb-3"
              @click="openAttachmentPicker"
              @dragover.prevent
              @drop.prevent="handleAttachmentDrop"
            >
              <v-icon color="primary" size="28">mdi-cloud-upload-outline</v-icon>
              <div class="mt-2 text-body-2 font-weight-medium">
                {{ $t("Drag and drop files here") }}
              </div>
              <div class="text-caption attachment-hint">
                {{ $t("or click to select a file") }}
              </div>
            </div>

            <file-picker
              ref="refFilePicker"
              :value="value"
              :label="pickerLabel"
              :multiple="multiple"
              :type-prop="typeProp"
              :val="val"
              @input="$emit('input', $event)"
            />

            <div class="attachment-table-toolbar mt-3">
              <div class="text-body-2 grey--text text--darken-1 attachment-count-badge">
                {{
                  hasFiles
                    ? `${attachmentRows.length} فایل انتخاب شده`
                    : "فایلی انتخاب نشده"
                }}
              </div>
              <v-btn
                v-if="attachmentRows.length > 1"
                outlined
                small
                color="error"
                class="clear-btn"
                :disabled="!hasFiles"
                @click="clearAllAttachments"
              >
                <v-icon small class="ml-1">mdi-trash-can-outline</v-icon>
                {{ $t("clearAll") }}
              </v-btn>
            </div>
          </div>

          <div class="attachment-table-scroll">
            <table class="attachment-table">
              <thead>
                <tr>
                  <th>{{ $t("preview") }}</th>
                  <th>{{ $t("fileName") }}</th>
                  <th>{{ $t("fileType") }}</th>
                  <th>{{ $t("size") }}</th>
                  <th>{{ $t("operations") }}</th>
                </tr>
              </thead>
              <tbody v-if="attachmentRows.length">
                <tr v-for="(row, index) in attachmentRows" :key="row.key">
                  <td>
                    <div class="preview-cell" @click="openAttachment(row)">
                      <img
                        v-if="row.isImage && row.link"
                        :src="row.link"
                        class="preview-img"
                        alt=""
                      >
                      <v-icon
                        v-else
                        :size="isMobile ? 36 : 22"
                        color="grey darken-1"
                      >
                        mdi-file-outline
                      </v-icon>
                    </div>
                  </td>
                  <td class="file-name-cell" :title="row.name">
                    {{ row.name }}
                  </td>
                  <td>{{ row.type }}</td>
                  <td>{{ row.sizeText }}</td>
                  <td>
                    <div class="amf-file-ops">
                      <button
                        type="button"
                        class="amf-file-ops__btn amf-file-ops__btn--open"
                        :aria-label="$t('download')"
                        @click="openAttachment(row)"
                      >
                        <v-icon :size="isMobile ? 22 : 18" color="#1565C0">
                          mdi-download-outline
                        </v-icon>
                      </button>
                      <button
                        type="button"
                        class="amf-file-ops__btn amf-file-ops__btn--delete"
                        :aria-label="$t('delete')"
                        @click="removeAttachment(index)"
                      >
                        <v-icon :size="isMobile ? 22 : 18" color="#E53935">
                          mdi-trash-can-outline
                        </v-icon>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="5" class="empty-row">
                    {{ $t("no-data-found") }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      top
      style="z-index: 1000000000000 !important;"
    >
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="snackbar = false"
        >
          {{ $t("close") }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import filePicker from "./filePicker/index";

const IMAGE_EXTS = [".jpg", ".jpeg", ".png", ".bmp", ".gif", ".webp"];

export default {
  name: "FormAttachmentDoc",
  components: {
    filePicker,
  },
  props: {
    value: {
      default: null,
    },
    typeProp: {
      type: String,
      required: true,
    },
    val: {
      default: null,
    },
    label: {
      type: String,
      default: "",
    },
    title: {
      type: String,
      default: "",
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    maxSize: {
      type: Number,
      default: 500000,
    },
    cardClass: {
      type: [String, Array, Object],
      default: "",
    },
  },
  data: () => ({
    snackbar: false,
    snackbarText: "",
    snackbarColor: "pink",
  }),
  computed: {
    headerTitle() {
      return this.title || this.$t("documents");
    },
    pickerLabel() {
      return this.label || this.$t("addDocuments");
    },
    hasFiles() {
      return Array.isArray(this.value) && this.value.length > 0;
    },
    attachmentRows() {
      if (!this.hasFiles) return [];
      return this.value.map((file, index) => {
        const name = file?.name || file?.title || this.$t("haveNot");
        const nameExt = name.includes(".")
          ? "." + String(name.split(".").pop() || "").replace(/\s/g, "").toLowerCase()
          : "";
        const extensionRaw = file?.extension || nameExt || "";
        const extension = String(extensionRaw || "")
          .replace(/\s/g, "")
          .toLowerCase();
        const type = nameExt || extension || file?.type || this.$t("haveNot");
        const size = Number(file?.size || 0);
        const link = file?.url || file?.link || null;
        const mime = String(file?.type || "").toLowerCase();
        const isImage =
          IMAGE_EXTS.includes(nameExt) ||
          IMAGE_EXTS.includes(extension) ||
          mime.startsWith("image/");
        return {
          key: file?._id || file?.id || `${name}-${index}`,
          name,
          type,
          sizeText: this.formatAttachmentSize(size),
          size,
          link,
          isImage,
        };
      });
    },
  },
  methods: {
    /** Keep parent save flows working: `$refs.x.create(id)`. */
    create(id) {
      this.$refs.refFilePicker?.create?.(id);
    },
    handleFileImport() {
      this.$refs.refFilePicker?.handleFileImport?.();
    },
    openAttachmentPicker() {
      this.handleFileImport();
    },
    syncFilePickerAttachments(files) {
      const fp = this.$refs.refFilePicker;
      if (!fp) return;
      const next = Array.isArray(files) && files.length ? files : null;
      fp.selectedFile = next;
      fp.selectedMultipleFile = next ? [...next] : [];
      if (!next && fp.$refs?.uploader) {
        fp.$refs.uploader.value = null;
      }
    },
    handleAttachmentDrop(e) {
      const files = Array.from(e.dataTransfer?.files || []);
      if (!files.length) return;
      const previous = Array.isArray(this.value) ? [...this.value] : [];
      files.forEach((file) => {
        if (file.size > this.maxSize) {
          this.snackbarText = this.$t("fileShouldBeLessThan500KB");
          this.snackbarColor = "pink";
          this.snackbar = true;
          return;
        }
        file.link = URL.createObjectURL(file);
        file.extension = ". " + file.name.split(".").pop();
        file.title = file.name.split(".")[0];
        file._id = file.size + "" + file.lastModified;
        file.iconURL = "";
        previous.push(file);
      });
      const next = previous.length ? previous : null;
      this.$emit("input", next);
      this.$nextTick(() => this.syncFilePickerAttachments(next));
    },
    clearAllAttachments() {
      this.$emit("input", null);
      this.$nextTick(() => this.syncFilePickerAttachments(null));
    },
    removeAttachment(index) {
      const fp = this.$refs.refFilePicker;
      if (fp && Array.isArray(fp.selectedFile) && fp.selectedFile[index]) {
        fp.remove(fp.selectedFile[index], index);
        return;
      }
      if (!Array.isArray(this.value)) return;
      const nextList = [...this.value];
      nextList.splice(index, 1);
      const next = nextList.length ? nextList : null;
      this.$emit("input", next);
      this.$nextTick(() => this.syncFilePickerAttachments(next));
    },
    openAttachment(row) {
      if (!row?.link) return;
      window.open(
        row.link,
        "_blank",
        "toolbar=yes,scrollbars=yes,resizable=yes,width=600,height=500"
      );
    },
    formatAttachmentSize(bytes) {
      if (!bytes || Number.isNaN(bytes)) return "-";
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
  },
};
</script>
